// frontend/src/services/api.js - Fixed with correct API prefixes
// ⚠️ CRITICAL: See API_ROUTING_GUIDE.md before making changes
// 
// RULES:
// 1. baseURL MUST always end with /api (normalized below)
// 2. All API calls MUST NOT include /api/ prefix (baseURL already has it)
// 3. Example: api.post('/auth/login') → https://crowapp.ca/api/auth/login
//
import axios from 'axios';
import logger from '../utils/logger';
import { clearSession as clearCorvinSession } from '../components/corvin/session';
import { photoFilename } from '../lib/photoFile';

const API_BASE_URL = process.env.REACT_APP_API_URL || '';

// =======================
// BaseURL Normalization - CRITICAL: DO NOT REMOVE
// =======================
// This ensures baseURL always ends with /api regardless of environment variable
// Handles both: API_URL=https://crowapp.ca and API_URL=https://crowapp.ca/api
let normalizedBaseURL = API_BASE_URL || '';
if (normalizedBaseURL && !normalizedBaseURL.endsWith('/api')) {
  // Remove trailing slash if present, then add /api
  normalizedBaseURL = normalizedBaseURL.replace(/\/$/, '') + '/api';
} else if (!normalizedBaseURL) {
  normalizedBaseURL = '/api';
}

// =======================
// Utility Functions (Updated for httpOnly Cookie Authentication)
// =======================
// Note: With httpOnly cookies, tokens are NOT accessible via JavaScript.
// Authentication is handled automatically by the browser via cookies.
// We only store user info and CSRF token in localStorage.
// =======================
export const apiUtils = {
  // Check if user is authenticated (based on stored user data)
  // Note: This is a client-side check only. Server validates via cookie.
  isAuthenticated: () => {
    return !!localStorage.getItem('user');
  },
  
  // Get current user from localStorage
  getCurrentUser: () => {
    const user = localStorage.getItem('user');
    return user ? JSON.parse(user) : null;
  },
  
  // Get CSRF token
  getCsrfToken: () => {
    return localStorage.getItem('csrf_token');
  },
  
  // Set auth data (user info + tokens for backward compatibility)
  setAuthData: (user, csrfToken = null, accessToken = null, refreshToken = null) => {
    localStorage.setItem('user', JSON.stringify(user));
    if (csrfToken) {
      localStorage.setItem('csrf_token', csrfToken);
    }
    // Store tokens for Bearer auth fallback (in case cookies don't work through proxy)
    if (accessToken) {
      localStorage.setItem('token', accessToken);
    }
    if (refreshToken) {
      localStorage.setItem('refresh_token', refreshToken);
    }
    return user;
  },
  
  // Update CSRF token after refresh
  updateCsrfToken: (csrfToken) => {
    if (csrfToken) {
      localStorage.setItem('csrf_token', csrfToken);
    }
  },
  
  // Clear auth data (user info only - cookies cleared by server)
  clearAuthData: () => {
    localStorage.removeItem('user');
    localStorage.removeItem('csrf_token');
    // Legacy cleanup
    localStorage.removeItem('token');
    localStorage.removeItem('refresh_token');
    // Corvin's cached tip can name this user's customers. Every way out of a
    // session (logout, 401 redirect, refresh failure) passes through here.
    clearCorvinSession();
  },
  
  // Format error message from API response
  formatErrorMessage: (error) => {
    if (error.response?.data?.detail) {
      return error.response.data.detail;
    }
    if (error.response?.data?.message) {
      return error.response.data.message;
    }
    if (error.message) {
      return error.message;
    }
    return 'An unexpected error occurred';
  }
};

// AI routes outlive the 30s default below. These match the read timeouts
// nginx gives them (nginx/nginx.prod.conf): a shorter client timeout makes
// the browser hang up and report an error while the server finishes -- and
// bills -- the call anyway (measured ~45s per quick diagnosis, 2026-10-06).
export const AI_TIMEOUT_MS = 120000;     // /api/ai, /api/diagnostics
export const AGENT_TIMEOUT_MS = 300000;  // /api/mechanic-agent

// Create axios instance with normalized baseURL
// ⚠️ CRITICAL: baseURL is normalized above to always end with /api
// All API calls below should NOT include /api/ prefix
export const api = axios.create({
  baseURL: normalizedBaseURL,
  headers: {
    'Content-Type': 'application/json',
  },
  // No timeout at all until #131: a slow endpoint hung the browser for eight
  // and a half minutes instead of surfacing an error. nginx cuts /api/ off at
  // 30s, so anything past that is already dead server-side.
  timeout: 30000,
  // CRITICAL: Include credentials (cookies) with all requests
  withCredentials: true,
});

// Request interceptor to add CSRF token for state-changing requests
api.interceptors.request.use(
  (config) => {
    // Add CSRF token for POST, PUT, DELETE, PATCH requests
    const method = config.method?.toUpperCase();
    if (['POST', 'PUT', 'DELETE', 'PATCH'].includes(method)) {
      const csrfToken = apiUtils.getCsrfToken();
      if (csrfToken) {
        config.headers['X-CSRF-Token'] = csrfToken;
      }
    }
    
    // Legacy support: Also check for Bearer token (for API clients)
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Track if we're currently refreshing to prevent multiple refresh calls
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve();
    }
  });
  failedQueue = [];
};

// Response interceptor for error handling with automatic token refresh
// NOTE: With httpOnly cookies, tokens are refreshed via cookies automatically
api.interceptors.response.use(
  (response) => {
    // Update CSRF token if present in response
    if (response.data?.csrf_token) {
      apiUtils.updateCsrfToken(response.data.csrf_token);
    }
    return response;
  },
  async (error) => {
    const originalRequest = error.config;
    logger.log('API Error:', error.response?.status, error.response?.data);
    
    if (error.response?.status === 401 && !originalRequest._retry) {
      // Check if this is a refresh endpoint failure (don't retry refresh)
      if (originalRequest.url?.includes('/auth/refresh')) {
        logger.log('Refresh failed - redirecting to login');
        apiUtils.clearAuthData();
        window.location.href = '/login';
        return Promise.reject(error);
      }

      // Check for MFA required
      if (error.response?.data?.mfa_required) {
        logger.log('MFA required');
        return Promise.reject(error);
      }

      if (isRefreshing) {
        // Wait for the ongoing refresh to complete
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(() => {
          // Retry with refreshed cookie (automatic)
          return api(originalRequest);
        }).catch(err => {
          return Promise.reject(err);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        logger.log('Attempting token refresh...');
        // Send refresh token in body as fallback (in case cookies don't work through proxy)
        const storedRefreshToken = localStorage.getItem('refresh_token');
        const response = await api.post('/auth/refresh', {
          refresh_token: storedRefreshToken
        });
        
        // Update CSRF token and tokens from response
        if (response.data?.csrf_token) {
          apiUtils.updateCsrfToken(response.data.csrf_token);
        }
        // Also store new tokens for Bearer auth fallback
        if (response.data?.access_token || response.data?.token) {
          localStorage.setItem('token', response.data.access_token || response.data.token);
        }
        if (response.data?.refresh_token) {
          localStorage.setItem('refresh_token', response.data.refresh_token);
        }
        
        logger.log('Token refresh successful');
        
        processQueue(null);
        
        // Retry the original request (cookie updated automatically)
        return api(originalRequest);
      } catch (refreshError) {
        logger.log('Token refresh failed:', refreshError.response?.data);
        processQueue(refreshError);
        apiUtils.clearAuthData();
        window.location.href = '/login';
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    } else if (error.response?.status === 403) {
      // Forbidden - might be subscription or permission issue
      logger.log('403 Forbidden - checking if subscription issue');
      // FastAPI wraps the error in detail, so check both paths
      const upgradeRequired = error.response?.data?.upgrade_required || 
                              error.response?.data?.detail?.upgrade_required;
      if (upgradeRequired) {
        // This is a subscription upgrade required, don't logout
        logger.log('Subscription upgrade required');
        return Promise.reject(error);
      } else {
        // Other 403 error - don't logout, just reject (403 is not an auth error)
        logger.log('403 Forbidden - permission denied');
        return Promise.reject(error);
      }
    }
    return Promise.reject(error);
  }
);

// =======================
// Authentication API
// =======================
export const authAPI = {
  login: async (credentials) => {
    try {
      // Clear any stale tokens BEFORE login to prevent race conditions
      apiUtils.clearAuthData();
      
      const response = await api.post('/auth/login', credentials);
      const { user, csrf_token, mfa_required, access_token, token, refresh_token } = response.data;
      
      // Check if MFA is required
      if (mfa_required) {
        return { mfa_required: true };
      }
      
      // Store user info, CSRF token, and auth tokens (for Bearer fallback)
      if (user) {
        apiUtils.setAuthData(user, csrf_token, access_token || token, refresh_token);
        // Give localStorage time to sync
        await new Promise(resolve => setTimeout(resolve, 100));
      }
      
      return response.data;
    } catch (error) {
      throw new Error(apiUtils.formatErrorMessage(error));
    }
  },
  
  // Verify MFA code
  verifyMfa: async (code, useBackup = false) => {
    try {
      const response = await api.post('/auth/verify-mfa', { 
        code, 
        use_backup: useBackup 
      });
      
      // Store tokens after successful MFA verification
      const { user, csrf_token, access_token, token, refresh_token } = response.data;
      if (user) {
        apiUtils.setAuthData(user, csrf_token, access_token || token, refresh_token);
      }
      
      return response.data;
    } catch (error) {
      throw new Error(apiUtils.formatErrorMessage(error));
    }
  },
  
  register: async (userData) => {
    const response = await api.post('/auth/register', userData);
    return response.data;
  },
  
  // New method for completing registration with subscription
  completeRegistration: async (data) => {
    const response = await api.post('/auth/complete-registration', {
      user_id: data.user_id,
      temp_token: data.temp_token,
      subscription_id: data.subscription_id,
      tier: data.tier,
      billing_interval: data.billing_interval
    });
    return response.data;
  },
  
  logout: async () => {
    // With httpOnly cookies, server handles token from cookie
    const response = await api.post('/auth/logout');
    return response.data;
  },
  
  getProfile: async () => {
    const response = await api.get('/auth/profile');
    return response.data;
  },
  
  updateProfile: async (profileData) => {
    const response = await api.put('/auth/profile', profileData);
    return response.data;
  },

  // Email verification endpoints
  resendEmailVerification: async (email) => {
    const response = await api.post('/auth/resend-verification', { email });
    return response.data;
  },

  verifyEmail: async (token) => {
    const response = await api.get(`/auth/verify-email?token=${token}`);
    return response.data;
  },

  getEmailVerificationStatus: async () => {
    const response = await api.get('/auth/email-status');
    return response.data;
  },

  // Phone verification endpoints (to be implemented in backend)
  sendPhoneVerification: async (phone) => {
    const response = await api.post('/auth/send-phone-verification', { phone });
    return response.data;
  },

  verifyPhone: async (phone, code) => {
    const response = await api.post('/auth/verify-phone', { phone, code });
    return response.data;
  },

  getPhoneVerificationStatus: async () => {
    const response = await api.get('/auth/phone-status');
    return response.data;
  },

  // Password reset endpoints
  forgotPassword: async (email) => {
    const response = await api.post('/auth/forgot-password', { email });
    return response.data;
  },

  resetPassword: async (token, newPassword) => {
    const response = await api.post('/auth/reset-password', { 
      token, 
      new_password: newPassword 
    });
    return response.data;
  }
};

authAPI.completeRegistration = async (data) => {
  const response = await api.post('/auth/complete-registration', {
    user_id: data.user_id,
    temp_token: data.temp_token,
    subscription_id: data.subscription_id,
    tier: data.tier,
    billing_interval: data.billing_interval
  });
  return response.data;
};

// =======================
// Vehicle Search API (for parts lookup)
// =======================
export const vehicleAPI = {
  searchVehicles: async (searchParams) => {
    const response = await api.post('/vehicles/search', searchParams);
    return response.data;
  },
  
  decodeVin: async (vin) => {
    const response = await api.post('/vin/decode', { vin });
    return response.data;
  }
};

// =======================
// Parts Search API
// =======================
export const partsAPI = {
  searchParts: async (searchParams) => {
    const response = await api.post('/parts/search', searchParams);
    return response.data;
  },
  
  getPartsCategories: async (make = null) => {
    const params = make ? { make } : {};
    const response = await api.get('/parts/categories', { params });
    return response.data;
  }
};

// =======================
// Vehicle Profile API - FIXED WITH /api PREFIX
// =======================
export const vehicleProfileAPI = {
  // Decode VIN for profile creation
  decodeVin: async (vin) => {
    logger.log('🔍 Decoding VIN via /api/vehicle-profiles/vin/decode');
    const response = await api.post('/vehicle-profiles/vin/decode', { vin });
    return response.data;
  },
  
  // Create a new vehicle profile
  createProfile: async (profileData) => {
    logger.log('🚗 Creating vehicle profile via /api/vehicle-profiles/');
    logger.log('Profile data:', profileData);
    const response = await api.post('/vehicle-profiles/', profileData);
    return response.data;
  },
  
  // Get all user's vehicle profiles
  getProfiles: async () => {
    logger.log('📋 Fetching vehicle profiles from /api/vehicle-profiles/');
    const response = await api.get('/vehicle-profiles/');
    logger.log('Response:', response.data);
    return response.data;
  },
  
  // Get a specific vehicle profile
  getProfile: async (vehicleId) => {
    logger.log(`🔍 Fetching vehicle ${vehicleId} from /api/vehicle-profiles/${vehicleId}`);
    const response = await api.get(`/vehicle-profiles/${vehicleId}`);
    return response.data;
  },
  
  // Update vehicle profile
  updateProfile: async (vehicleId, updateData) => {
    logger.log(`✏️ Updating vehicle ${vehicleId} via /api/vehicle-profiles/${vehicleId}`);
    const response = await api.put(`/vehicle-profiles/${vehicleId}`, updateData);
    return response.data;
  },
  
  // Delete vehicle profile
  deleteProfile: async (vehicleId) => {
    logger.log(`🗑️ Deleting vehicle ${vehicleId} via /api/vehicle-profiles/${vehicleId}`);
    const response = await api.delete(`/vehicle-profiles/${vehicleId}`);
    return response.data;
  },
  
  // Update just the mileage (quick update)
  updateMileage: async (vehicleId, mileage) => {
    logger.log(`🛣️ Updating mileage for vehicle ${vehicleId} to ${mileage}`);
    const response = await api.put(`/vehicle-profiles/${vehicleId}`, { mileage });
    return response.data;
  }
};

// =======================
// Job Management API
// =======================
export const jobAPI = {
  // Get all jobs for current user
  getJobs: async () => {
    const response = await api.get('/jobs/');
    return response.data;
  },
  
  // Create a new job
  createJob: async (jobData) => {
    const response = await api.post('/jobs/', jobData);
    return response.data;
  },
  
  // Get a specific job
  getJob: async (jobId) => {
    const response = await api.get(`/jobs/${jobId}`);
    return response.data;
  },
  
  // Update job
  updateJob: async (jobId, jobData) => {
    const response = await api.put(`/jobs/${jobId}`, jobData);
    return response.data;
  },
  
  // Delete job
  deleteJob: async (jobId) => {
    const response = await api.delete(`/jobs/${jobId}`);
    return response.data;
  },
  
  // Search jobs with filters
  searchJobs: async (searchParams) => {
    const response = await api.post('/jobs/search', searchParams);
    return response.data;
  },
  
  // Get dashboard statistics
  getDashboardStats: async () => {
    const response = await api.get('/jobs/stats/dashboard');
    return response.data;
  },
  
  // Run diagnosis on a job
  runDiagnosis: async (jobId, diagnosisData) => {
    const response = await api.post(`/diagnostics/${jobId}/quick-diagnose`, diagnosisData, { timeout: AI_TIMEOUT_MS });
    return response.data;
  },

  // Customer management
  getCustomersList: async (params = {}) => {
    const response = await api.get('/jobs/customers/list', { params });
    return response.data;
  },

  getCustomerJobs: async (customerName, params = {}) => {
    const response = await api.get(`/jobs/customers/${encodeURIComponent(customerName)}/jobs`, { params });
    return response.data;
  },

  // Get suggested repairs for a job
  getSuggestedRepairs: async (jobId) => {
    const response = await api.get(`/jobs/${jobId}/suggested-repairs`);
    return response.data;
  },

  // Get suggested maintenance for a job
  getSuggestedMaintenance: async (jobId) => {
    const response = await api.get(`/jobs/${jobId}/suggested-maintenance`);
    return response.data;
  },

  // Get existing diagnosis results for a job
  getDiagnosticHistory: async (jobId) => {
    const response = await api.get(`/diagnostics/${jobId}/history`);
    return response.data;
  },
  
  // Get vehicle data for a job (maintenance schedules, repairs, etc.)
  getJobVehicleData: async (jobId) => {
    const response = await api.get(`/jobs/${jobId}/vehicle-data`);
    return response.data;
  },

  // Manually trigger vehicle data collection for a job
  collectVehicleData: async (jobId) => {
    const response = await api.post(`/jobs/${jobId}/collect-vehicle-data`);
    return response.data;
  },

  // Update job status
  updateJobStatus: async (jobId, statusData) => {
    const response = await api.patch(`/jobs/${jobId}/status`, statusData);
    return response.data;
  },

  // Generate invoice for a job
  generateInvoice: async (jobId, invoiceData = {}) => {
    const response = await api.post(`/jobs/${jobId}/invoice`, invoiceData);
    return response.data;
  },

  // Get invoice information for a job
  getJobInvoice: async (jobId) => {
    const response = await api.get(`/jobs/${jobId}/invoice`);
    return response.data;
  },
  
  // Save invoice to database
  saveJobInvoice: async (jobId, invoiceData) => {
    const response = await api.post(`/jobs/${jobId}/invoice/save`, invoiceData);
    return response.data;
  },

  // Estimate approval link: the raw url comes back ONLY from create (tokens are hashed)
  createShareLink: async (jobId, { send_email = false } = {}) => {
    const response = await api.post(`/jobs/${jobId}/invoice/share-link`, { send_email });
    return response.data;
  },

  revokeShareLink: async (jobId) => {
    await api.delete(`/jobs/${jobId}/invoice/share-link`);
  },

  // Customer approvals of the job's estimate (append-only on the server)
  recordInvoiceAuthorization: async (jobId, data) => {
    const response = await api.post(`/jobs/${jobId}/invoice/authorizations`, data);
    return response.data;
  },

  // Turn the job's saved estimate into an invoice
  convertInvoice: async (jobId) => {
    const response = await api.post(`/jobs/${jobId}/invoice/convert`);
    return response.data;
  },
  
  // Generate and download PDF invoice with optional custom values (GET with query params)
  generateInvoicePDF: async (jobId, options = {}) => {
    const params = new URLSearchParams();
    if (options.labor_hours !== undefined) params.append('labor_hours', options.labor_hours);
    if (options.labor_rate !== undefined) params.append('labor_rate', options.labor_rate);
    if (options.tax_rate !== undefined) params.append('tax_rate', options.tax_rate);
    if (options.discount !== undefined) params.append('discount', options.discount);
    if (options.discount_type !== undefined) params.append('discount_type', options.discount_type);
    
    const queryString = params.toString();
    const url = `/jobs/${jobId}/invoice/pdf${queryString ? `?${queryString}` : ''}`;
    
    const response = await api.get(url, {
      responseType: 'blob'
    });
    return response.data;
  },
  
  // Generate and download PDF invoice with full data (POST with body - supports edited parts)
  generateInvoicePDFWithData: async (jobId, invoiceData) => {
    const response = await api.post(`/jobs/${jobId}/invoice/pdf`, invoiceData, {
      responseType: 'blob'
    });
    return response.data;
  },
  
  // Get all saved invoices for a job
  getJobInvoices: async (jobId) => {
    const response = await api.get(`/jobs/${jobId}/invoices`);
    return response.data;
  },
  
  // Get user's default job/invoice preferences
  getUserJobPreferences: async () => {
    const response = await api.get('/jobs/preferences/invoice-defaults');
    return response.data;
  },
  
  // Update user's default job/invoice preferences
  updateUserJobPreferences: async (preferences) => {
    const response = await api.post('/jobs/preferences/invoice-defaults', preferences);
    return response.data;
  },
  
  // Get all invoices for the current user (across all jobs)
  getAllUserInvoices: async (page = 1, pageSize = 20, status = null) => {
    const params = new URLSearchParams({ page, page_size: pageSize });
    if (status) params.append('status', status);
    const response = await api.get(`/jobs/documents/invoices?${params.toString()}`);
    return response.data;
  },
  
  // Get all estimates for the current user (across all jobs)
  getAllUserEstimates: async (page = 1, pageSize = 20) => {
    const params = new URLSearchParams({ page, page_size: pageSize });
    const response = await api.get(`/jobs/documents/estimates?${params.toString()}`);
    return response.data;
  },
  
  // Get all AI diagnostics for the current user
  getAllUserDiagnostics: async (page = 1, pageSize = 20) => {
    const params = new URLSearchParams({ page, page_size: pageSize });
    const response = await api.get(`/jobs/diagnostics/all?${params.toString()}`);
    return response.data;
  },
  
  // Send invoice to customer via email
  sendInvoiceEmail: async (jobId, invoiceData) => {
    const response = await api.post(`/jobs/${jobId}/invoice/send`, invoiceData);
    return response.data;
  },
  
  // Update invoice status
  updateInvoiceStatus: async (invoiceId, status) => {
    const response = await api.patch(`/jobs/invoices/${invoiceId}/status`, { status });
    return response.data;
  },

  // Mechanic-agent AI drafts (Task 6). Proposals only — nothing is written
  // to the job by these calls; the mechanic reviews and applies (or
  // discards) the result client-side. Note: no `/api` prefix — baseURL
  // already ends in `/api` (see header comment above).
  draftDiagnosis: async (jobId, symptom) => {
    const response = await api.post(`/mechanic-agent/jobs/${jobId}/draft-diagnosis`, { symptom }, { timeout: AGENT_TIMEOUT_MS });
    return response.data;
  },

  draftEstimate: async (jobId, symptom) => {
    const response = await api.post(`/mechanic-agent/jobs/${jobId}/draft-estimate`, { symptom }, { timeout: AGENT_TIMEOUT_MS });
    return response.data;
  }
};

// Pre-job customer intake. A "customer" elsewhere in CROW is a DISTINCT
// customer_name over jobs; these are people who have no job yet.
export const inquiryAPI = {
  create: async (payload) => {
    const response = await api.post('/inquiries', payload);
    return response.data;
  },

  list: async (params = {}) => {
    const response = await api.get('/inquiries', { params });
    return response.data;
  },

  update: async (inquiryId, payload) => {
    const response = await api.patch(`/inquiries/${inquiryId}`, payload);
    return response.data;
  },

  convert: async (inquiryId, payload = {}) => {
    const response = await api.post(`/inquiries/${inquiryId}/convert`, payload);
    return response.data;
  },

  close: async (inquiryId) => {
    const response = await api.post(`/inquiries/${inquiryId}/close`);
    return response.data;
  },
};

// Job photos. Upload is multipart. The shared `api` instance defaults to
// application/json, and axios turns a FormData body into JSON under that
// header, so the upload must say multipart/form-data explicitly (axios then
// adds the boundary), as the vehicle document uploads below do.
export const jobPhotoAPI = {
  list: async (jobId) => {
    const response = await api.get(`/jobs/${jobId}/photos`);
    return response.data;
  },

  upload: async (jobId, blob, filename, note, onProgress) => {
    const formData = new FormData();
    formData.append('file', blob, photoFilename(filename, blob.type));
    if (note) formData.append('note', note);
    const response = await api.post(`/jobs/${jobId}/photos`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      // Phone photos over a weak connection can take far longer than 30s.
      timeout: 120000,
      onUploadProgress: (e) => {
        if (onProgress && e.total) onProgress(e.loaded / e.total);
      },
    });
    return response.data;
  },

  getBlob: async (jobId, photoId) => {
    const response = await api.get(`/jobs/${jobId}/photos/${photoId}`, {
      responseType: 'blob',
    });
    return response.data;
  },

  updateNote: async (jobId, photoId, note) => {
    const response = await api.patch(`/jobs/${jobId}/photos/${photoId}`, { note });
    return response.data;
  },

  remove: async (jobId, photoId) => {
    await api.delete(`/jobs/${jobId}/photos/${photoId}`);
  },
};

// =======================
// Health Check
// =======================
export const healthAPI = {
  check: async () => {
    const response = await api.get('/health');
    return response.data;
  }
};

// Add these to your existing api.js file

// =======================
// Enhanced VIN Decoder API
// =======================
export const vinAPI = {
  // Decode VIN using enhanced endpoint
  decode: async (vin) => {
    const response = await api.get(`/vin/decode/${vin}`);
    return response.data;
  },
  
  // Get compatible parts for a VIN
  getCompatibleParts: async (vin, params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.category) queryParams.append('category', params.category);
    if (params.limit) queryParams.append('limit', params.limit);
    
    const url = `/vin/decode/${vin}/compatible-parts${queryParams.toString() ? `?${queryParams}` : ''}`;
    const response = await api.get(url);
    return response.data;
  },
  
  // Validate VIN format
  validate: async (vin) => {
    const response = await api.get(`/vin/validate/${vin}`);
    return response.data;
  },
  
  // Batch decode multiple VINs
  batchDecode: async (vins) => {
    const response = await api.post('/vin/decode/batch', { vins });
    return response.data;
  }
};

// =======================
// Enhanced Parts API (Phase 1)
// =======================
export const enhancedPartsAPI = {
  // Get categories with counts
  getCategories: async () => {
    const response = await api.get('/categories');
    return response.data;
  },
  
  // Get manufacturers with counts
  getManufacturers: async () => {
    const response = await api.get('/manufacturers');
    return response.data;
  },
  
  // Get enhanced parts with JSONB
  getEnhancedParts: async (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.limit) queryParams.append('limit', params.limit);
    if (params.category) queryParams.append('category', params.category);
    if (params.universal_only) queryParams.append('universal_only', params.universal_only);
    if (params.min_warranty) queryParams.append('min_warranty', params.min_warranty);
    
    // Note: This uses the main API for enhanced parts
    const response = await api.get(`/parts/enhanced?${queryParams}`);
    return response.data;
  },
  
  // Search by specifications
  searchBySpec: async (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.material) queryParams.append('material', params.material);
    if (params.min_warranty) queryParams.append('min_warranty', params.min_warranty);
    if (params.max_weight_kg) queryParams.append('max_weight_kg', params.max_weight_kg);
    
    // Note: This uses the main API for spec search
    const response = await api.get(`/parts/search-by-spec?${queryParams}`);
    return response.data;
  }
};

// =======================
// Debug API
// =======================
export const debugAPI = {
  // Get parts count and statistics
  getPartsCount: async () => {
    const response = await api.get('/debug/parts-count');
    return response.data;
  }
};

export const vehicleHealthAPI = {
  // Create a vehicle issue report
  createVehicleIssue: async (issueData) => {
    logger.log('🚨 Creating vehicle issue via /api/vehicle-profiles/issues');
    const response = await api.post(`/vehicle-profiles/${issueData.vehicle_profile_id}/issues`, issueData);
    return response.data;
  },
  
  // Get vehicle issues for a vehicle
  getVehicleIssues: async (vehicleId, filters = {}) => {
    logger.log(`📋 Fetching vehicle issues for vehicle ${vehicleId}`);
    const params = new URLSearchParams();
    if (filters.status) params.append('status', filters.status);
    if (filters.severity) params.append('severity', filters.severity);

    const response = await api.get(
      `/vehicle-profiles/${vehicleId}/issues?${params}`
    );
    return response.data;
  },

  // Update a vehicle issue
  updateVehicleIssue: async (issueId, updates) => {
    logger.log(`✏️ Updating vehicle issue ${issueId}`);
    const response = await api.put(
      `/vehicle-profiles/issues/${issueId}`,
      updates
    );
    return response.data;
  },

  // Delete a vehicle issue
  deleteVehicleIssue: async (issueId) => {
    logger.log(`🗑️ Deleting vehicle issue ${issueId}`);
    const response = await api.delete(
      `/api/vehicle-profiles/issues/${issueId}`
    );
    return response.data;
  },

  // Create a maintenance log with AI analysis
  createMaintenanceLog: async (logData) => {
    logger.log('🔧 Creating maintenance log via /api/vehicle-health/maintenance-logs');
    const response = await api.post('/vehicle-health/maintenance-logs', logData);
    return response.data;
  },
  
  // Get maintenance logs for a vehicle
  getMaintenanceLogs: async (vehicleId, filters = {}) => {
    logger.log(`📋 Fetching maintenance logs for vehicle ${vehicleId}`);
    const params = new URLSearchParams();
    if (filters.resolved !== undefined) params.append('resolved', filters.resolved);
    if (filters.log_type) params.append('log_type', filters.log_type);

    const response = await api.get(
      `/vehicle-health/maintenance-logs/${vehicleId}?${params}`
    );
    return response.data;
  },

  // Update/resolve a maintenance log
  updateMaintenanceLog: async (logId, updates) => {
    logger.log(`✏️ Updating maintenance log ${logId}`);
    const response = await api.put(
      `/vehicle-health/maintenance-logs/${logId}`,
      updates
    );
    return response.data;
  },

  // Get vehicle health score with AI recommendations
  getHealthScore: async (vehicleId) => {
    logger.log(`💯 Fetching health score for vehicle ${vehicleId}`);
    const response = await api.get(
      `/vehicle-health/health-score/${vehicleId}`
    );
    return response.data;
  },

  // Helper to format severity for display
  getSeverityInfo: (severity) => {
    const severityMap = {
      'critical': { color: 'red', bgColor: 'red', text: 'Critical', icon: '🚨' },
      'major': { color: 'orange', bgColor: 'orange', text: 'Major', icon: '⚠️' },
      'moderate': { color: 'yellow', bgColor: 'yellow', text: 'Moderate', icon: '⚡' },
      'minor': { color: 'blue', bgColor: 'blue', text: 'Minor', icon: 'ℹ️' }
    };
    return severityMap[severity] || { color: 'gray', bgColor: 'gray', text: 'Unknown', icon: '❓' };
  },

  // Helper to format health status
  getHealthStatusInfo: (status) => {
    const statusMap = {
      // Only `text` is ever read (VehicleList). The emoji and gradient
      // fields that sat here rendered nowhere and are gone with the banned
      // gradient health cards.
      'excellent': { text: 'Excellent' },
      'good': { text: 'Good' },
      'fair': { text: 'Fair' },
      'poor': { text: 'Poor' },
      'critical': { text: 'Critical' }
    };
    return statusMap[status] || { text: 'Unknown' };
  },

  // =======================
  // Admin API Functions
  // =======================
  
  // Get admin analytics
  getAnalytics: async () => {
    console.log('📊 Fetching admin analytics');
    const response = await api.get('/admin/analytics');
    return response.data;
  },

  // Get all users (admin only)
  getAllUsers: async () => {
    console.log('👥 Fetching all users');
    const response = await api.get('/admin/users');
    return response.data;
  },

  // Delete a user (admin only)
  deleteUser: async (userId) => {
    console.log(`🗑️ Deleting user ${userId}`);
    const response = await api.delete(`/admin/users/${userId}`);
    return response.data;
  },

  // Update a user (admin only)
  updateUser: async (userId, updates) => {
    console.log(`✏️ Updating user ${userId}`);
    const response = await api.put(`/admin/users/${userId}`, updates);
    return response.data;
  },

  // Get system statistics
  getSystemStats: async () => {
    console.log('📈 Fetching system statistics');
    const response = await api.get('/admin/stats');
    return response.data;
  }
};

// Add admin functions to the default export
api.getAnalytics = async () => {
  logger.log('📊 Fetching admin analytics');
  const response = await api.get('/admin/analytics');
  return response.data;
};

api.getAllUsers = async () => {
  logger.log('👥 Fetching all users');
  const response = await api.get('/admin/users');
  return response.data;
};

api.deleteUser = async (userId) => {
  logger.log(`🗑️ Deleting user ${userId}`);
  const response = await api.delete(`/admin/users/${userId}`);
  return response.data;
};

api.updateUser = async (userId, updates) => {
  logger.log(`✏️ Updating user ${userId}`);
  const response = await api.put(`/admin/users/${userId}`, updates);
  return response.data;
};

api.getSystemStats = async () => {
  logger.log('📈 Fetching system statistics');
  const response = await api.get('/admin/stats');
  return response.data;
};

// Add these sections to your existing api.js file
// =======================
// Subscription Management API
// =======================
export const subscriptionAPI = {
  // Get available subscription plans
  getPlans: async () => {
    const response = await api.get('/subscriptions/plans');
    return response.data;
  },
  
  // Get current user subscription
  getCurrentSubscription: async () => {
    const response = await api.get('/subscriptions/current');
    return response.data;
  },
  
  // Check if user can perform action (search limit)
  checkLimit: async () => {
    const response = await api.post('/subscriptions/check-limit');
    return response.data;
  },
  
  // Increment usage counter
  incrementUsage: async () => {
    const response = await api.post('/subscriptions/increment-usage');
    return response.data;
  },
  
  // Get usage statistics
  getUsageStats: async () => {
    const response = await api.get('/subscriptions/usage');
    return response.data;
  },
  
  // Get billing history
  getBillingHistory: async () => {
    const response = await api.get('/subscriptions/billing-history');
    return response.data;
  },
  
  // Cancel subscription
  cancelSubscription: async () => {
    const response = await api.post('/subscriptions/cancel');
    return response.data;
  },
  
  // Get trial status
  getTrialStatus: async () => {
    const response = await api.get('/subscriptions/trial-status');
    return response.data;
  },
  
};

// =======================
// Payment Management API
// =======================
export const paymentAPI = {
  // Create setup intent for saving payment methods
  createSetupIntent: async () => {
    const response = await api.post('/payments/create-setup-intent');
    return response.data;
  },

  // Save payment method after setup
  savePaymentMethod: async (data) => {
    const response = await api.post('/payments/save-payment-method', {
      payment_method_id: data.payment_method_id,
      setup_intent_id: data.setup_intent_id
    });
    return response.data;
  },
  
  // Get saved payment methods
  getPaymentMethods: async () => {
    const response = await api.get('/payments/payment-methods');
    return response.data;
  },

  // Invoices from Stripe, the source of truth: { data: [...] }. Was wired to
  // a function that did not exist here, behind a route that did not exist.
  getBillingHistory: async () => {
    const response = await api.get('/payments/billing-history');
    return response.data;
  },
  
  // Create subscription with payment
  createSubscription: async (data) => {
    const response = await api.post('/payments/create-subscription', {
      tier: data.tier,
      billing_interval: data.billing_interval || 'month',
      payment_method_id: data.payment_method_id,
      promo_code: data.promo_code
    });
    return response.data;
  },
  
  // Change subscription tier or billing
  changeSubscription: async (data) => {
    const response = await api.post('/payments/change-subscription', {
      new_tier: data.new_tier,
      new_billing_interval: data.new_billing_interval || 'month'
    });
    return response.data;
  },
  
  // Cancel subscription
  cancelSubscription: async () => {
    const response = await api.post('/payments/cancel-subscription');
    return response.data;
  },
  // Resume subscription (turn auto-renew on)
  resumeSubscription: async () => {
    const response = await api.post('/payments/resume-subscription');
    return response.data;
  },
  
  // Track pricing experiment (for A/B testing)
  trackPricingExperiment: async (data) => {
    const response = await api.post('/payments/pricing-experiment', data);
    return response.data;
  }
};

// =======================
// Promotional Codes API
// =======================
export const promoCodeAPI = {
  // Validate a promo code (authenticated)
  validate: async (data) => {
    const response = await api.post('/promo-codes/validate', {
      code: data.code,
      tier: data.tier,
      billing_interval: data.billing_interval || 'month'
    });
    return response.data;
  },
  
  // Validate a promo code (public - for signup flow)
  validatePublic: async (data) => {
    const response = await api.post('/promo-codes/validate-public', {
      code: data.code,
      tier: data.tier,
      billing_interval: data.billing_interval || 'month',
      user_type: data.user_type || 'car_owner'
    });
    return response.data;
  },
  
  // Apply a promo code
  apply: async (data) => {
    const response = await api.post('/promo-codes/apply', {
      code: data.code,
      tier: data.tier,
      billing_interval: data.billing_interval || 'month',
      stripe_subscription_id: data.stripe_subscription_id
    });
    return response.data;
  },
  
  // Get popular promo codes
  getPopular: async () => {
    const response = await api.get('/promo-codes/popular');
    return response.data;
  },
  
  // Get user's promo code history
  getUserCodes: async () => {
    const response = await api.get('/promo-codes/my-codes');
    return response.data;
  },
  
  // Generate referral code
  generateReferralCode: async () => {
    const response = await api.get('/promo-codes/referral/generate');
    return response.data;
  },
  
  // Get referral statistics
  getReferralStats: async () => {
    const response = await api.get('/promo-codes/referral/stats');
    return response.data;
  }
};

// =======================
// Updated Authentication API
// =======================
// Add this to your existing authAPI or replace the existing one:


// =======================
// Helper Functions for Subscription Flow
// =======================
export const subscriptionHelpers = {
  // Format subscription tier for display
  formatTierName: (tier) => {
    const tierNames = {
      'free': 'Free Trial',
      'basic': 'Basic',
      'professional': 'Professional',
      'shop_pro': 'Shop Pro',
      'enterprise': 'Enterprise'
    };
    return tierNames[tier] || tier;
  },
  
  // Get tier badge color
  getTierColor: (tier) => {
    const tierColors = {
      'free': 'gray',
      'basic': 'blue',
      'professional': 'purple',
      'shop_pro': 'indigo',
      'enterprise': 'red'
    };
    return tierColors[tier] || 'gray';
  },
  
  // Calculate price with promo discount
  calculateDiscountedPrice: (originalPrice, discountType, discountValue) => {
    if (discountType === 'percentage') {
      return originalPrice * (1 - discountValue / 100);
    } else if (discountType === 'fixed_amount') {
      return Math.max(0, originalPrice - discountValue);
    } else if (discountType === 'free_trial') {
      return 0; // First period free
    }
    return originalPrice;
  },
  
  // Format price for display
  formatPrice: (price, interval = 'month') => {
    if (price === 0) return 'Free';
    const formatted = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(price);
    return `${formatted}/${interval}`;
  },
  
  // Check if subscription is active
  isSubscriptionActive: (subscription) => {
    return subscription && 
           subscription.status === 'active' && 
           !subscription.cancelled && 
           !subscription.payment_failed;
  },
  
  // Get days remaining in trial
  getTrialDaysRemaining: (trialEndDate) => {
    if (!trialEndDate) return 0;
    const end = new Date(trialEndDate);
    const now = new Date();
    const diffTime = end - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return Math.max(0, diffDays);
  }
};

// =======================
// Consolidated API Export
// =======================
// Add these to your existing exports
export const enhancedAPI = {
  ...api,
  auth: { ...authAPI },
  subscription: subscriptionAPI,
  payment: paymentAPI,
  promoCode: promoCodeAPI,
  helpers: {
    ...apiUtils,
    ...subscriptionHelpers
  }
};

const vehicleMaintenanceAPI = {
  // Get maintenance schedule
  getMaintenanceSchedule: async (vehicleProfileId, forceRefresh = false) => {
    return api.get(`/vehicle/maintenance/${vehicleProfileId}`, {
      params: { force_refresh: forceRefresh }
    });
  },

  // Update mileage
  updateMileage: async (vehicleProfileId, newMileage) => {
    return api.put(`/vehicle/maintenance/${vehicleProfileId}/mileage`, {
      new_mileage: newMileage
    });
  },

  // Mark service completed
  completeService: async (vehicleProfileId, serviceData) => {
    return api.post(`/vehicle/maintenance/${vehicleProfileId}/complete`, serviceData);
  },

  // Update service status
  updateServiceStatus: async (scheduleId, status, notes = null) => {
    return api.put(`/vehicle/maintenance/service/${scheduleId}/status`, {
      status,
      notes
    });
  },

  // Get maintenance history
  getMaintenanceHistory: async (vehicleProfileId, limit = 50) => {
    return api.get(`/vehicle/maintenance/${vehicleProfileId}/history`, {
      params: { limit }
    });
  }
};

// For backward compatibility, also export individual functions on the default api object
api.createSubscription = paymentAPI.createSubscription;
api.validatePromoCode = promoCodeAPI.validate;
api.getSubscriptionPlans = subscriptionAPI.getPlans;
api.savePaymentMethod = paymentAPI.savePaymentMethod;
api.createSetupIntent = paymentAPI.createSetupIntent;

// =======================
// Vehicle Documents API
// =======================
export const vehicleDocumentsAPI = {
  // Get all documents for a vehicle
  getDocuments: async (vehicleId) => {
    const response = await api.get(`/vehicle-profiles/${vehicleId}/documents`);
    return response.data;
  },
  
  // Upload a new document
  uploadDocument: async (vehicleId, formData) => {
    const response = await api.post(`/vehicle-profiles/${vehicleId}/documents`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },
  
  // Delete a document
  deleteDocument: async (vehicleId, documentId) => {
    const response = await api.delete(`/vehicle-profiles/${vehicleId}/documents/${documentId}`);
    return response.data;
  },
  
  // Download a document
  downloadDocument: async (vehicleId, documentId) => {
    const response = await api.get(`/vehicle-profiles/${vehicleId}/documents/${documentId}/download`, {
      responseType: 'blob'
    });
    return response.data;
  },
  
  // Get profile picture for a vehicle
  getProfilePicture: async (vehicleId) => {
    const response = await api.get(`/vehicle-profiles/${vehicleId}/profile-picture`);
    return response.data;
  },
  
  // Upload profile picture for a vehicle
  uploadProfilePicture: async (vehicleId, formData) => {
    const response = await api.post(`/vehicle-profiles/${vehicleId}/profile-picture`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },
  
  // Delete profile picture for a vehicle
  deleteProfilePicture: async (vehicleId) => {
    const response = await api.delete(`/vehicle-profiles/${vehicleId}/profile-picture`);
    return response.data;
  }
};

// =======================
// Notification API
// =======================
export const notificationAPI = {
  // Get user's notification settings
  getSettings: async () => {
    const response = await api.get('/notifications/settings');
    return response.data;
  },
  
  // Update notification settings
  updateSettings: async (settings) => {
    const response = await api.put('/notifications/settings', settings);
    return response.data;
  },
  
  // Get notification history
  getHistory: async (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.limit) queryParams.append('limit', params.limit);
    if (params.type) queryParams.append('type', params.type);
    if (params.read) queryParams.append('read', params.read);
    
    const url = `/notifications/history${queryParams.toString() ? `?${queryParams}` : ''}`;
    const response = await api.get(url);
    return response.data;
  },
  
  // Mark notification as read
  markAsRead: async (notificationId) => {
    const response = await api.put(`/notifications/${notificationId}/read`);
    return response.data;
  },
  
  // Mark all notifications as read
  markAllAsRead: async () => {
    const response = await api.put('/notifications/mark-all-read');
    return response.data;
  },
  
  // Delete a notification
  deleteNotification: async (notificationId) => {
    const response = await api.delete(`/notifications/${notificationId}`);
    return response.data;
  },
  
  // Test notification (for testing settings)
  testNotification: async (type = 'email') => {
    const response = await api.post('/notifications/test', { type });
    return response.data;
  },
  
  // Get notification preferences for specific features
  getPreferences: async (feature) => {
    const response = await api.get(`/notifications/preferences/${feature}`);
    return response.data;
  },
  
  // Update notification preferences for specific features
  updatePreferences: async (feature, preferences) => {
    const response = await api.put(`/notifications/preferences/${feature}`, preferences);
    return response.data;
  }
};

// Add auth methods to the default api export for backward compatibility
api.auth = authAPI;
api.resendEmailVerification = authAPI.resendEmailVerification;
api.verifyEmail = authAPI.verifyEmail;
api.getEmailVerificationStatus = authAPI.getEmailVerificationStatus;
api.sendPhoneVerification = authAPI.sendPhoneVerification;
api.verifyPhone = authAPI.verifyPhone;
api.getPhoneVerificationStatus = authAPI.getPhoneVerificationStatus;

// Add notification methods to the default api export for backward compatibility
// Note: These use notificationAPI which already has /api prefix
api.getNotificationSettings = notificationAPI.getSettings;
api.updateNotificationSettings = notificationAPI.updateSettings;
api.getNotificationHistory = notificationAPI.getHistory;
api.markNotificationAsRead = notificationAPI.markAsRead;
api.markAllNotificationsAsRead = notificationAPI.markAllAsRead;
api.deleteNotification = notificationAPI.deleteNotification;
api.testNotification = notificationAPI.testNotification;

// Add payment and billing methods to the default api export
api.getPaymentMethods = paymentAPI.getPaymentMethods;
api.savePaymentMethod = paymentAPI.savePaymentMethod;
api.deletePaymentMethod = async (paymentMethodId) => {
  const response = await api.delete(`/payments/payment-methods/${paymentMethodId}`);
  return response.data;
};
api.getBillingHistory = paymentAPI.getBillingHistory;
api.cancelSubscription = paymentAPI.cancelSubscription;
api.resumeSubscription = paymentAPI.resumeSubscription;

// =======================
// Public estimate link (customer, no login)
// =======================
// A SEPARATE axios instance: no auth/refresh interceptors, no credentials, so a
// 401/404 from a customer's link is handed back to the page and can never
// bounce them to the CROW login screen.
export const publicEstimateClient = axios.create({
  baseURL: normalizedBaseURL,
  headers: { 'Content-Type': 'application/json' },
  timeout: 30000,
});

const browserTimeZone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || null;
  } catch {
    return null;
  }
};

export const publicEstimateAPI = {
  get: async (token) =>
    (await publicEstimateClient.get(`/public/estimates/${encodeURIComponent(token)}`)).data,
  // timezone: the server stores the approval time as the customer's wall clock.
  approve: async (token, name, expectedTotal) =>
    (await publicEstimateClient.post(`/public/estimates/${encodeURIComponent(token)}/approve`, {
      name, expected_total: expectedTotal, timezone: browserTimeZone(),
    })).data,
  decline: async (token, reason, expectedTotal) =>
    (await publicEstimateClient.post(`/public/estimates/${encodeURIComponent(token)}/decline`, {
      reason, expected_total: expectedTotal,
    })).data,
  // Asks the server for a card checkout for whatever is due; it decides the amount.
  pay: async (token) =>
    (await publicEstimateClient.post(`/public/estimates/${encodeURIComponent(token)}/pay`)).data,
};

// Default export for backward compatibility
export default api;
// Canned jobs: a shop's saved parts + hours + work text, added to estimates.
export const cannedJobAPI = {
  list: async () => {
    const response = await api.get('/canned-jobs');
    return response.data;
  },

  create: async (job) => {
    const response = await api.post('/canned-jobs', job);
    return response.data;
  },

  update: async (id, job) => {
    const response = await api.put(`/canned-jobs/${id}`, job);
    return response.data;
  },

  remove: async (id) => {
    await api.delete(`/canned-jobs/${id}`);
  },
};

// Shop payments: the mechanic's own Stripe account, and payments on a job.
export const shopPaymentAPI = {
  getAccount: async () => (await api.get('/shop-payments/account')).data,
  // country: 'US' | 'CA'. Only used when the shop has no Stripe account yet.
  connect: async (country) =>
    (await api.post('/shop-payments/account/connect', country ? { country } : {})).data,
  getPayments: async (jobId) => (await api.get(`/jobs/${jobId}/payments`)).data,
  recordPayment: async (jobId, body) =>
    (await api.post(`/jobs/${jobId}/payments`, body)).data,
};
